/**
 * Editing a call, and the line the editing must not cross.
 *
 * Every AI deed tool in the field ships an editable call table. Ours were
 * read-only because editing collides with what the product sells: the
 * attestation refuses a modified payload, so a hand-edited extraction loses
 * its certificate. The service resolves that — `/v1/corrections` applies the
 * change, recomputes, and re-signs — and this module is the browser half:
 * which fields may be edited, how a typed value becomes one, and what a value
 * has to look like before it is worth posting.
 *
 * **The distinction the UI has to keep visible.** A *correction* says the
 * document reads something else and we misread it, so the verdict is
 * recomputed and means what it always meant. An *adjustment* says the recorded
 * courses do not close and here is a best fit, so the verdict does not move.
 * `closable` below is deliberately not automatic: an adjustment nobody asked
 * for is the silent auto-close this product exists to refuse.
 *
 * Parsing lives here rather than in the component so it can be tested as
 * arithmetic — the portal has no DOM test environment.
 */

import { bearingText } from "./format";
import type {
  BearingModel,
  Call,
  CorrectableField,
  ServerVerdict,
} from "./types";

export type FieldKind = "number" | "bearing" | "word";

export interface EditableField {
  field: CorrectableField;
  label: string;
  kind: FieldKind;
}

const LINE_FIELDS: EditableField[] = [
  { field: "bearing", label: "bearing", kind: "bearing" },
  { field: "distance", label: "distance", kind: "number" },
];

const CURVE_FIELDS: EditableField[] = [
  { field: "chord_bearing", label: "chord bearing", kind: "bearing" },
  { field: "chord_length", label: "chord length", kind: "number" },
  { field: "radius", label: "radius", kind: "number" },
  { field: "arc_length", label: "arc length", kind: "number" },
  { field: "curve_direction", label: "curve direction", kind: "word" },
];

/**
 * What may be edited on this call.
 *
 * `verbatim_text` is never here. It is the document's own words, and a
 * correction able to rewrite the quoted source would make the certificate's
 * diff meaningless.
 */
export function editableFields(call: Call): EditableField[] {
  return call.call_type === "curve" ? CURVE_FIELDS : LINE_FIELDS;
}

/** The value a field currently holds, as the editor shows it. */
export function currentValue(call: Call, field: CorrectableField): unknown {
  return (call as unknown as Record<string, unknown>)[field] ?? null;
}

/** The same value as editable text. */
export function currentText(call: Call, field: CorrectableField): string {
  const value = currentValue(call, field);
  if (value === null || value === undefined) return "";
  if (typeof value === "object") return bearingText(value as BearingModel);
  return String(value);
}

export class BadValue extends Error {}

const BEARING =
  /^([NS])\s*(\d+(?:\.\d+)?)\s*(?:°|d|deg)?\s*(?:(\d+)\s*(?:'|m|min)?\s*)?(?:(\d+(?:\.\d+)?)\s*(?:"|s|sec)?\s*)?([EW])$/i;

/**
 * A typed quadrant bearing.
 *
 * Accepts what a surveyor types rather than one canonical spelling: `N 87°30'
 * 00" E`, `N87 30 00 E`, `S 4d 12m E`, and degrees with a decimal instead of
 * minutes. What it will not do is guess at something it cannot read — an
 * unparseable bearing throws, because the alternative is posting a confident
 * number nobody typed.
 */
export function parseBearing(text: string): BearingModel {
  const match = BEARING.exec(text.trim().replace(/\s+/g, " "));
  if (!match) {
    throw new BadValue(
      `"${text}" is not a bearing. Write it as a quadrant, e.g. N 87°30'00" E.`,
    );
  }
  const [, ns, rawDegrees, rawMinutes, rawSeconds, ew] = match;
  let degrees = Number(rawDegrees);
  let minutes = Number(rawMinutes ?? 0);
  let seconds = Number(rawSeconds ?? 0);
  // Decimal degrees with nothing after them: N 87.5 E is 87°30'.
  if (!rawMinutes && !Number.isInteger(degrees)) {
    const fraction = (degrees - Math.floor(degrees)) * 60;
    degrees = Math.floor(degrees);
    minutes = Math.floor(fraction);
    seconds = Math.round((fraction - minutes) * 60 * 10) / 10;
  }
  if (degrees > 90 || minutes >= 60 || seconds >= 60) {
    throw new BadValue(
      `"${text}" is outside a quadrant: degrees run 0-90, minutes and seconds 0-59.`,
    );
  }
  return {
    ns: ns.toUpperCase() as "N" | "S",
    degrees,
    minutes,
    seconds,
    ew: ew.toUpperCase() as "E" | "W",
  };
}

/** A typed value as the wire wants it, or a refusal naming what went wrong. */
export function parseValue(kind: FieldKind, text: string): unknown {
  const trimmed = text.trim();
  if (!trimmed) return null;
  if (kind === "bearing") return parseBearing(trimmed);
  if (kind === "word") {
    const word = trimmed.toLowerCase();
    if (word !== "left" && word !== "right") {
      throw new BadValue(
        `Curve direction is "left" or "right", not "${text}".`,
      );
    }
    return word;
  }
  const number = Number(trimmed);
  if (!Number.isFinite(number)) {
    throw new BadValue(`"${text}" is not a number.`);
  }
  return number;
}

/** Whether a typed value actually differs from what the call already holds. */
export function changed(before: unknown, after: unknown): boolean {
  if (before === null || before === undefined) return after !== null;
  if (after === null) return true;
  if (typeof before === "object" || typeof after === "object") {
    return JSON.stringify(before) !== JSON.stringify(after);
  }
  if (typeof before === "number" && typeof after === "number") {
    return Math.abs(before - after) > 1e-9;
  }
  return String(before) !== String(after);
}

/**
 * A tract worth offering to close.
 *
 * Three conditions, and each rules out a fit that would mean nothing. There
 * has to be geometry, because no fit rescues a traverse that failed outright.
 * The closure cannot already be exact, which is what a null denominator says.
 * And the verdict cannot be PASS: a description the service accepted needs no
 * best fit, and offering one there invites a disclosed adjustment onto a
 * certificate that had nothing to disclose.
 *
 * Reading the verdict rather than a tolerance keeps the threshold in one
 * place. The server decides what closes well enough; this only asks.
 */
export function closable(verdict: ServerVerdict | undefined): boolean {
  const geometry = verdict?.geometry;
  if (!geometry) return false;
  if (geometry.closure_denominator === null) return false;
  if (verdict?.status === "PASS") return false;
  return geometry.misclosure > 0;
}

/** How much a fit would move, in words, so the button says what it will do. */
export function closeLabel(verdict: ServerVerdict, mark: string): string {
  const misclosure = verdict.geometry?.misclosure ?? 0;
  return `Close the shape · distributes ${misclosure.toFixed(2)} ${mark} of misclosure`;
}
